import React, { useEffect, useMemo } from "react";
import { useWarehouses } from "../../../features/inventory/hooks/useWarehouses";
import { useWarehouseProducts } from "../../../features/inventory/hooks/useWarehouseProducts";
import { useSaveProductInventory } from "../../../features/inventory/hooks/useSaveProductInventory";
import { useBulkUpdateInventory } from "../../../features/inventory/hooks/useBulkUpdateInventory";
import { useExportInventory } from "../../../features/inventory/hooks/useExportInventory";
import { useInventoryStore } from "../../../store/useInventoryStore";

import WarehouseSelector from "./WarehouseSelector";
import InventoryToolbar from "./InventoryToolbar";
import InventoryTable from "./InventoryTable";
import InventoryExcelImportModal from "./InventoryExcelImportModal";
import InventoryBulkPreviewModal from "./InventoryBulkPreviewModal";

import { prepareBulkUpdatePayload } from "../../../utils/inventory/inventoryExcel";
import Pagination from "../../Pagination";


const SupplierInventoryPage = () => {
  const selectedWarehouseId = useInventoryStore((state) => state.selectedWarehouseId);
  const page = useInventoryStore((state) => state.page);
  const setPage = useInventoryStore((state) => state.setPage);
  const pageSize = useInventoryStore((state) => state.pageSize);

  const excelModalOpen = useInventoryStore((state) => state.excelModalOpen);
  const bulkPreviewOpen = useInventoryStore((state) => state.bulkPreviewOpen);
  const bulkPreviewRows = useInventoryStore((state) => state.bulkPreviewRows || []);
  const setBulkPreviewRows = useInventoryStore((state) => state.setBulkPreviewRows);
  const closeExcelModal = useInventoryStore((state) => state.closeExcelModal);
  const closeBulkPreview = useInventoryStore((state) => state.closeBulkPreview);
  const replaceRowDrafts = useInventoryStore((state) => state.replaceRowDrafts);
  const resetAllDrafts = useInventoryStore((state) => state.resetAllDrafts);
  const setSelectedWarehouseId = useInventoryStore((state) => state.setSelectedWarehouseId);
  const savingRows = useInventoryStore((state) => state.savingRows || {});

  const search = useInventoryStore((state) => state.search);
  const applyFilters = useInventoryStore((state) => state.applyFilters);
  const clearFilters = useInventoryStore((state) => state.clearFilters);


  const {
    warehouses = [],
    isLoading: warehousesLoading,
    isError: warehousesError,
    error: warehousesErrorObj,
  } = useWarehouses();

  const {
    products = [],
    totalPages,
    isLoading: productsLoading,
    isFetching: productsFetching,
    isError: productsError,
    error: productsErrorObj,
    refetch,
  } = useWarehouseProducts();

  const saveRowMutation = useSaveProductInventory();
  const bulkUpdateMutation = useBulkUpdateInventory();
  const exportMutation = useExportInventory();

  useEffect(() => {
    if (!products || products.length === 0) {
      resetAllDrafts();
      return;
    }

    const draftsMap = {};
    products.forEach(product => {
      draftsMap[product.id] = {
        price: product.price ?? "",
        stock: product.stock ?? "",
        discount_percent: product.discount_percent ?? "",
        min_order: product.min_order ?? 1,
        max_order: product.max_order ?? "",
        order_multiple: product.order_multiple ?? 1,
        inventory_is_active: product.inventory_is_active ?? true,
        originalData: {
          price: product.price ?? "",
          stock: product.stock ?? "",
          discount_percent: product.discount_percent ?? "",
          min_order: product.min_order ?? 1,
          max_order: product.max_order ?? "",
          order_multiple: product.order_multiple ?? 1,
          inventory_is_active: product.inventory_is_active ?? true,
        },
        isDirty: false,
      };
    });

    replaceRowDrafts(draftsMap);
  }, [products]);

  const selectedWarehouse = useMemo(() => {
    return warehouses.find((w) => String(w.id) === String(selectedWarehouseId)) || null;
  }, [warehouses, selectedWarehouseId]);

  const handleBulkSubmit = async () => {
    if (!selectedWarehouseId || !bulkPreviewRows.length) return;

    const payload = prepareBulkUpdatePayload(
      bulkPreviewRows,
      selectedWarehouseId
    );

    await bulkUpdateMutation.mutateAsync(payload);

    closeBulkPreview();
    closeExcelModal();
    setBulkPreviewRows([]);
    refetch();
  };

  const handleExportExcel = async () => {
    try {
      await exportMutation.mutateAsync();
    } catch (error) {
      // خطا توسط onError هوک مدیریت می‌شود
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    applyFilters();
  };

  const handleClear = () => {
    clearFilters();
  };


  return (
    <div className="space-y-4">
      <div className="rounded-xl border bg-white p-4 shadow-sm">
        <h1 className="mb-4 text-xl font-bold">مدیریت موجودی تأمین‌کننده</h1>

        <WarehouseSelector
          warehouses={warehouses}
          selectedWarehouseId={selectedWarehouseId}
          onChange={setSelectedWarehouseId}
          isLoading={warehousesLoading}
          error={warehousesError ? warehousesErrorObj : null}
        />
      </div>

      <div className="rounded-xl border bg-white p-4 shadow-sm">
        <InventoryToolbar
          selectedWarehouse={selectedWarehouse}
          onSubmit={handleExportExcel}
          onSearch={handleSearch}
          onClear={handleClear}
          loading={productsLoading || productsFetching}
        />

        <InventoryTable
          products={products}
          loading={productsLoading}
          fetching={productsFetching}
          error={productsError ? productsErrorObj : null}
          onSaveRow={(rowId, payload) =>
            saveRowMutation.mutateAsync({
              warehouseId: selectedWarehouseId,
              productId: rowId,
              payload,
            })
          }
          savingRows={savingRows}
        />
      </div>
      <Pagination page={page} setPage={setPage} totalPages={totalPages}/>

      <InventoryExcelImportModal
        open={excelModalOpen}
        onClose={closeExcelModal}
      />

      <InventoryBulkPreviewModal
        open={bulkPreviewOpen}
        rows={bulkPreviewRows}
        submitting={bulkUpdateMutation.isPending}
        onClose={closeBulkPreview}
        onSubmit={handleBulkSubmit}
        onRowsChange={setBulkPreviewRows}
      />
    </div>
  );
};

export default SupplierInventoryPage;
